import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

export type LauncherSide = 'left' | 'right';

export type LauncherPosition = {
	horizontalPadding: number;
	side: LauncherSide;
	verticalPadding: number;
};

// Pixels the pointer must move before a press becomes a drag rather than a click.
const DRAG_THRESHOLD = 5;
const VIEWPORT_MARGIN = 8;

function readStored(storageKey: string): LauncherPosition | null {
	if (typeof window === 'undefined') return null;
	try {
		const raw = window.localStorage.getItem(storageKey);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as Partial<LauncherPosition>;
		if (
			(parsed.side === 'left' || parsed.side === 'right') &&
			typeof parsed.horizontalPadding === 'number' &&
			typeof parsed.verticalPadding === 'number'
		) {
			return { horizontalPadding: parsed.horizontalPadding, side: parsed.side, verticalPadding: parsed.verticalPadding };
		}
	} catch {
		// Ignore malformed/inaccessible storage.
	}
	return null;
}

export function clampPosition(pos: LauncherPosition, launcherSize: number): LauncherPosition {
	if (typeof window === 'undefined') return pos;
	const maxHorizontal = Math.max(VIEWPORT_MARGIN, window.innerWidth - launcherSize - VIEWPORT_MARGIN);
	const maxVertical = Math.max(VIEWPORT_MARGIN, window.innerHeight - launcherSize - VIEWPORT_MARGIN);
	return {
		horizontalPadding: Math.min(Math.max(pos.horizontalPadding, VIEWPORT_MARGIN), maxHorizontal),
		side: pos.side,
		verticalPadding: Math.min(Math.max(pos.verticalPadding, VIEWPORT_MARGIN), maxVertical),
	};
}

export type UseDraggableLauncherOptions = {
	/** Position used until the user drags the button (a stored position always wins once set). Re-read when its fields change. */
	defaultPosition: LauncherPosition;
	/** Rendered button size in px; used to keep the whole button clamped on-screen. */
	launcherSize: number;
	/** Called on a press that did not move far enough to count as a drag. */
	onPress?: () => void;
	/** localStorage key the per-device position is persisted under. */
	storageKey: string;
};

/**
 * Shared behavior for a fixed, freely-draggable launcher button (the custom Intercom launcher, the
 * Glazier Assistant button, …). Owns the per-device persisted {side, horizontalPadding, verticalPadding}
 * position, the pointer-drag gesture (with a click/drag threshold), and the resulting CSS offsets.
 *
 * Spread `pointerHandlers` onto the draggable element and apply `style` to the positioned element — the
 * button itself, or, for a popover-anchored launcher, its anchor (the panel then follows for free). A
 * press that isn't a drag calls `onPress`. A drag leaves `wasDragRef.current` true so the consumer can
 * swallow the synthetic click the drag produces (e.g. `onClickCapture` on a Radix trigger) before the
 * next pointer-down clears it.
 */
export default function useDraggableLauncher({ defaultPosition, launcherSize, onPress, storageKey }: UseDraggableLauncherOptions) {
	const { horizontalPadding: defaultHorizontal, side: defaultSide, verticalPadding: defaultVertical } = defaultPosition;

	const [pos, setPosState] = useState<LauncherPosition>(defaultPosition);
	const [isLoaded, setIsLoaded] = useState(false);
	const [dragXY, setDragXY] = useState<{ left: number; top: number } | null>(null);

	const wasDragRef = useRef(false);
	const dragXYRef = useRef<{ left: number; top: number } | null>(null);
	const startRef = useRef<{ offsetX: number; offsetY: number; x: number; y: number } | null>(null);
	const onPressRef = useRef(onPress);
	onPressRef.current = onPress;

	// Load the stored position (or the provided default) once mounted, and again whenever the default
	// changes while the user has not yet dragged the button (a stored position always wins once set).
	useEffect(() => {
		const stored = readStored(storageKey);
		const fallback: LauncherPosition = { horizontalPadding: defaultHorizontal, side: defaultSide, verticalPadding: defaultVertical };
		setPosState(clampPosition(stored ?? fallback, launcherSize));
		setIsLoaded(true);
	}, [storageKey, launcherSize, defaultHorizontal, defaultSide, defaultVertical]);

	// Keep the button on-screen when the viewport shrinks.
	useEffect(() => {
		function onResize() {
			setPosState((prev) => clampPosition(prev, launcherSize));
		}
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, [launcherSize]);

	const setPos = useCallback(
		(next: LauncherPosition) => {
			const clamped = clampPosition(next, launcherSize);
			setPosState(clamped);
			try {
				window.localStorage.setItem(storageKey, JSON.stringify(clamped));
			} catch {
				// Ignore write failures (e.g. private browsing).
			}
		},
		[launcherSize, storageKey]
	);

	const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
		const rect = e.currentTarget.getBoundingClientRect();
		startRef.current = { offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top, x: e.clientX, y: e.clientY };
		wasDragRef.current = false;
		e.currentTarget.setPointerCapture(e.pointerId);
	}, []);

	const onPointerMove = useCallback(
		(e: ReactPointerEvent<HTMLElement>) => {
			const start = startRef.current;
			if (!start) return;
			if (!wasDragRef.current && Math.hypot(e.clientX - start.x, e.clientY - start.y) < DRAG_THRESHOLD) return;
			wasDragRef.current = true;
			const next = {
				left: Math.min(Math.max(e.clientX - start.offsetX, 0), window.innerWidth - launcherSize),
				top: Math.min(Math.max(e.clientY - start.offsetY, 0), window.innerHeight - launcherSize),
			};
			dragXYRef.current = next;
			setDragXY(next);
		},
		[launcherSize]
	);

	const onPointerUp = useCallback(
		(e: ReactPointerEvent<HTMLElement>) => {
			startRef.current = null;
			if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);

			const dropped = dragXYRef.current;
			if (wasDragRef.current && dropped) {
				const centerX = dropped.left + launcherSize / 2;
				const side: LauncherSide = centerX < window.innerWidth / 2 ? 'left' : 'right';
				setPos({
					horizontalPadding: side === 'left' ? dropped.left : window.innerWidth - (dropped.left + launcherSize),
					side,
					verticalPadding: window.innerHeight - (dropped.top + launcherSize),
				});
				dragXYRef.current = null;
				setDragXY(null);
				// Leave wasDragRef true so the consumer can swallow the click this drag produces.
				return;
			}

			wasDragRef.current = false;
			onPressRef.current?.();
		},
		[launcherSize, setPos]
	);

	// The browser fires pointercancel instead of pointerup when a gesture is interrupted (incoming call,
	// lost pointer capture, …). Abort the in-progress drag: drop the live offset so the button reverts to
	// its committed position, and clear the refs so the next press starts clean. No click follows a cancel,
	// so wasDragRef goes back to false and onPress is not called.
	const onPointerCancel = useCallback((e: ReactPointerEvent<HTMLElement>) => {
		startRef.current = null;
		dragXYRef.current = null;
		setDragXY(null);
		wasDragRef.current = false;
		if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
	}, []);

	const anchoredStyle: CSSProperties =
		pos.side === 'left'
			? { bottom: pos.verticalPadding, left: pos.horizontalPadding }
			: { bottom: pos.verticalPadding, right: pos.horizontalPadding };
	const style: CSSProperties = dragXY ? { left: dragXY.left, top: dragXY.top } : anchoredStyle;

	return {
		dragXY,
		isLoaded,
		launcherSize,
		pointerHandlers: { onPointerCancel, onPointerDown, onPointerMove, onPointerUp },
		pos,
		setPos,
		style,
		wasDragRef,
	};
}
